'use client';

import React, { useRef, forwardRef, useId, useLayoutEffect, useState, cloneElement, isValidElement } from 'react';
import camelCase from 'lodash/camelCase';
import cn from 'classnames';
import useCombinedRef from '@creditflowresearch/uilib/hooks/useCombinedRef';
import useIsTouchDevice from '@creditflowresearch/uilib/hooks/useIsTouchDevice';
import Floating, {
  Placement,
  IFloatingProps,
  IFloatingRef,
  IReferenceElementProps,
} from '@creditflowresearch/uilib/Floating';
import styles from './tooltip.module.scss';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

const toggleDescribedBy = (element: Element, id: string, isAdded: boolean): void => {
  const ids = (element.getAttribute('aria-describedby') ?? '').split(' ').filter((value) => value && value !== id);
  const nextIds = isAdded ? [...ids, id] : ids;

  if (nextIds.length) {
    element.setAttribute('aria-describedby', nextIds.join(' '));
  } else {
    element.removeAttribute('aria-describedby');
  }
};

export type TooltipPlacement = Placement;
export interface ITooltipRef extends IFloatingRef {}

export type TooltipListType = 'bullet' | 'number';

export interface ITooltipListItem {
  id: string | number,
  text: React.ReactNode,
  icon?: React.ReactNode,
}

export interface ITooltipTableRow {
  id: string | number,
  icon?: React.ReactNode,
  cells: React.ReactNode[],
}

export interface ITooltipListContent {
  format: 'list',
  listType?: TooltipListType,
  items: ITooltipListItem[],
}

export interface ITooltipListGroup {
  id: string | number,
  title: React.ReactNode,
  items: ITooltipListItem[],
}

/**
 * A list split into titled groups. Unlike a flat list it shows every item and wraps long text,
 * since a group is usually the whole of something (e.g. every bank of a syndicate role).
 */
export interface ITooltipGroupedListContent {
  format: 'list',
  groups: ITooltipListGroup[],
  dataTest?: string,
}

export interface ITooltipTableContent {
  format: 'table',
  columns?: React.ReactNode[],
  rows: ITooltipTableRow[],
}

export type TooltipContent = React.ReactNode | ITooltipListContent | ITooltipGroupedListContent | ITooltipTableContent;

/**
 * Content built only once the tooltip opens. Worth it when the content is expensive to build
 * and the tooltip is rare, e.g. a cell in a wide table that assembles a list of links.
 */
export type LazyTooltipContent = () => TooltipContent;

// --- Sub-components ---

function TooltipText({ content }: { content: React.ReactNode }) {
  return <span className={ styles.textFormat }>{ content }</span>;
}

function TooltipList({ content }: { content: ITooltipListContent }) {
  const listType = content.listType ?? 'bullet';
  const listItems = content.items;
  const ListTag = listType === 'bullet' ? 'ul' : 'ol';

  return (
    <div
      className={ cn(styles.listWrapper, styles[listType]) }
      role="list"
      aria-label="Tooltip list"
    >
      <ListTag>
        { listItems.slice(0, 10).map((item, index) => (
          <li key={ item.id } className={ cn(styles.listItem, { [styles.hasIcon]: !!item.icon }) }>
            { listType === 'number' && <span className={ styles.listNumber }>{ index + 1 }</span> }
            { item.icon && <span className={ styles.icon }>{ item.icon }</span> }
            <span className={ styles.listText }>{ item.text }</span>
          </li>
        )) }
        { listItems.length > 10 && (
          <li key="limit-indicator" className={ cn(styles.listItem, styles.limitRow) }>
            { listType === 'number' && <span className={ styles.listNumber } /> }
            <span className={ styles.listText }>+{ listItems.length - 10 } more</span>
          </li>
        ) }
      </ListTag>
    </div>
  );
}

function TooltipGroupedList({ content }: { content: ITooltipGroupedListContent }) {
  const titleIdPrefix = useId();

  return (
    <div className={ cn(styles.listWrapper, styles.grouped) } data-test={ content.dataTest }>
      { content.groups.map((group, groupIndex) => {
        const titleId = `${ titleIdPrefix }-${ groupIndex }`;

        return (
          <div key={ group.id }>
            <span id={ titleId } className={ styles.listGroupTitle }>{ group.title }</span>
            <ul aria-labelledby={ titleId }>
              { group.items.map((item) => (
                <li key={ item.id } className={ cn(styles.listItem, { [styles.hasIcon]: !!item.icon }) }>
                  { item.icon && <span className={ styles.icon }>{ item.icon }</span> }
                  <span className={ styles.listText }>{ item.text }</span>
                </li>
              )) }
            </ul>
          </div>
        );
      }) }
    </div>
  );
}

function TooltipTable({ content }: { content: ITooltipTableContent }) {
  const tableColumns = content.columns ?? [];
  const tableRows = content.rows;
  const hasRowIcons = tableRows.some((row) => row.icon);

  return (
    <table className={ styles.tableWrapper } aria-label="Tooltip table">
      { tableColumns.length > 0 && (
        <thead>
          <tr>
            { hasRowIcons && <th className={ styles.iconCol } aria-label="Icon" /> }
            { tableColumns.map((col, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <th key={ idx } className={ idx === 0 ? styles.firstCol : styles.otherCol }>{ col }</th>
            )) }
          </tr>
        </thead>
      ) }
      <tbody>
        { tableRows.map((row) => (
          <tr key={ row.id }>
            { hasRowIcons && (
              <td className={ styles.iconCol } aria-hidden="true">{ row.icon }</td>
            ) }
            { row.cells.map((cell, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <td key={ idx } className={ idx === 0 ? styles.firstCol : styles.otherCol }>{ cell }</td>
            )) }
          </tr>
        )) }
      </tbody>
    </table>
  );
}

// --- asChild helpers ---

type TEventHandler = (...args: unknown[]) => void;

interface IAsChildElementProps {
  ref?: React.Ref<HTMLElement>,
  onClick?: (event: React.MouseEvent<HTMLElement>) => void,
  [key: string]: unknown,
}

const isEventHandler = (value: unknown): value is TEventHandler => typeof value === 'function';

/** Floating's reference props, with each `on*` handler chained after the child's own one of the same name. */
const mergeReferenceProps = (
  childProps: IAsChildElementProps,
  referenceProps: Record<string, unknown>,
): Record<string, unknown> => Object.fromEntries(
  Object.entries(referenceProps).map(([key, handler]) => {
    const childHandler = childProps[key];

    if (!key.startsWith('on') || !isEventHandler(handler) || !isEventHandler(childHandler)) {
      return [key, handler];
    }

    return [key, (...args: unknown[]) => {
      childHandler(...args);
      handler(...args);
    }];
  }),
);

// --- Main component ---

interface IBaseProps {
  /**
   * Defines the tooltip's trigger element
   */
  children: React.ReactNode,
  /**
   * Defines the maximum width of the tooltip in pixels. If the content is wider than the maxWidth,
   * it wraps into multiple lines.
   */
  maxWidth?: string | number,
  /**
   * Applies dark background styles to the tooltip
   */
  dark?: boolean,
  /**
   * Allows showing the tooltip when hovering over a disabled trigger element with the mouse
   */
  disabled?: boolean,
  /**
   * Applies a CSS class to the component's root element
   */
  className?: string,
  /**
   * Applies a CSS class to the wrapper element of the component
   */
  wrpClassName?: string,
  /**
   * Specifies the CSS class name for the container element of the component
   */
  containerClassName?: string,
  /**
   * The placement of the tooltip relative to its anchor element
   */
  placement?: TooltipPlacement,
  /**
   * Enables fixed positioning for the tooltip
   */
  positionFixed?: boolean,
  /**
   * The boundaries element for the tooltip's positioning
   */
  boundary?: IFloatingProps['positioning']['boundary'],
  /**
   * Keeps the tooltip in the DOM even when it's not visible
   */
  persistInDom?: boolean,
  /**
   * Renders the tooltip using a portal
   */
  usePortal?: boolean,
  /**
   * Options for offsetting the popover position
   * This can be used to adjust the distance between the popover and its trigger element.
   */
  offsetOptions?: IFloatingProps['positioning']['offsetOptions'],
  /**
   * Hides the tooltip when clicking outside of it
   */
  hideOnClick?: boolean,
  /**
   * Hides the tooltip when the mouse enters the tooltip itself. By default the tooltip stays open
   * while hovered, so its content can be read, selected, copied and clicked.
   */
  hideOnTooltipMouseEnter?: boolean,
  /**
   * Boolean indicating whether to enable preventing overflow of the tooltip content
   */
  preventOverflowEnabled?: boolean,
  /**
   * Defines the type of the tooltip, which can be a default or error type
   */
  type?: 'default' | 'error',
  /**
   * Sets the z-index of the tooltip
   */
  zIndex?: number,
  /**
   * Custom styles for the container element
   */
  containerStyles?: React.CSSProperties,
  /**
   * Attaches the tooltip to the child element itself instead of wrapping it in spans, so the tooltip does not
   * change layout (e.g. a full-width button stays the flex item). The child must be a single element that
   * forwards `ref` and props to the DOM (uilib `Button`, framework `Link`, native elements).
   * Not for disabled buttons: they receive no mouse events, so the tooltip would never open — keep the
   * default wrapper there. `wrpClassName`, `containerClassName`, `containerStyles` and the error container
   * style are ignored; requires `usePortal` (default).
   */
  asChild?: boolean,
  /**
   * Determines whether to show the tooltip based on custom logic
   */
  shouldShowTooltip?: ({ container }: { container: HTMLElement }) => boolean,
}

export interface IProps extends IBaseProps {
  /**
   * Defines the tooltip content.
   * - `ReactNode` — plain text or JSX (default).
   * - `{ format: 'list', items: ITooltipListItem[], listType?: 'bullet' | 'number' }` — structured list.
   * - `{ format: 'list', groups: ITooltipListGroup[] }` — list split into titled groups.
   * - `{ format: 'table', rows: ITooltipTableRow[], columns?: ReactNode[] }` — structured table.
   * - `() => TooltipContent` — any of the above, built only once the tooltip opens.
   */
  content: TooltipContent | LazyTooltipContent,
}

const Tooltip = forwardRef(
  (
    props: IProps,
    ref: React.ForwardedRef<ITooltipRef>,
  ) => {
    const {
      type = 'default',
      disabled = false,
      persistInDom = false,
      children,
      hideOnClick = false,
      maxWidth = '300px',
      className,
      wrpClassName,
      offsetOptions = {
        mainAxis: 5,
      },
      containerClassName,
      placement = 'bottom',
      dark = false,
      positionFixed,
      boundary,
      usePortal = true,
      hideOnTooltipMouseEnter = false,
      preventOverflowEnabled = true,
      zIndex,
      containerStyles,
      shouldShowTooltip = () => true,
      asChild = false,
    } = props;

    const isTouchDevice = useIsTouchDevice();

    const containerRef = useRef<HTMLElement>(null);
    const { combinedRef } = useCombinedRef();
    const [isTriggerFocusable, setIsTriggerFocusable] = useState(false);

    /**
     * Keyboard users must be able to reach the tooltip: when the trigger has nothing focusable
     * (an icon, truncated text, a disabled button), the trigger itself takes focus.
     * With `asChild` the trigger is the child, so it is checked along with its descendants — but only until it
     * gets our own `tabindex`, which would otherwise make it match and flip the state back on every render.
     */
    useLayoutEffect(() => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      const hasFocusable = (!isTriggerFocusable && container.matches(FOCUSABLE_SELECTOR))
        || !!container.querySelector(FOCUSABLE_SELECTOR);

      setIsTriggerFocusable(!disabled && !hasFocusable && shouldShowTooltip({ container }));
    });

    if (disabled && !persistInDom) {
      return children;
    }

    const renderTooltipContent = () => {
      const content = typeof props.content === 'function' ? props.content() : props.content;

      if (content != null && typeof content === 'object' && 'format' in content) {
        if (content.format === 'list') {
          return 'groups' in content
            ? <TooltipGroupedList content={ content } />
            : <TooltipList content={ content } />;
        }

        if (content.format === 'table') {
          return <TooltipTable content={ content } />;
        }
      }

      return <TooltipText content={ content as React.ReactNode } />;
    };

    const ARROW_EDGE_OFFSET = 8;
    const ARROW_SIZE = 10;

    const computedOffsetOptions: IFloatingProps['positioning']['offsetOptions'] = ({ placement: currentPlacement, rects }) => {
      const mainAxis = typeof offsetOptions === 'number' ? offsetOptions : (offsetOptions as { mainAxis?: number })?.mainAxis ?? 0;
      const isStart = currentPlacement.endsWith('-start');
      const isEnd = currentPlacement.endsWith('-end');

      if (!isStart && !isEnd) {
        return { mainAxis };
      }

      const isVertical = currentPlacement.startsWith('left') || currentPlacement.startsWith('right');
      const refSize = isVertical ? rects.reference.height : rects.reference.width;
      const arrowCenter = ARROW_EDGE_OFFSET + ARROW_SIZE / 2;
      const refCenter = refSize / 2;
      const shift = refCenter - arrowCenter;
      const crossAxis = isStart ? shift : -shift;

      return { mainAxis, crossAxis };
    };

    const handleReferenceClick = (
      event: React.MouseEvent<HTMLElement>,
      { referenceProps, context }: IReferenceElementProps,
    ): void => {
      event.nativeEvent.stopImmediatePropagation();

      if (hideOnClick) {
        if (context.open) {
          context.onOpenChange(false);
        }
      } else if (typeof referenceProps.onClick === 'function') {
        referenceProps.onClick(event);
      }
    };

    const asChildElement = asChild && isValidElement<IAsChildElementProps>(children) ? children : null;

    const renderReference = (referenceElementProps: IReferenceElementProps): React.ReactElement => {
      const { context } = referenceElementProps;

      /**
       * `aria-describedby` lands on the trigger, but screen readers announce the focused element,
       * so a focused child inside the trigger gets the tooltip id as well.
       */
      const describeFocusedChild = (event: React.FocusEvent<HTMLElement>, isAdded: boolean): void => {
        if (event.target !== event.currentTarget && context.floatingId) {
          toggleDescribedBy(event.target, context.floatingId, isAdded);
        }
      };

      const referenceProps: IReferenceElementProps['referenceProps'] = {
        ...referenceElementProps.referenceProps,
        ...(isTriggerFocusable && { tabIndex: 0 }),
        onFocus: (event: React.FocusEvent<HTMLElement>) => {
          const { onFocus } = referenceElementProps.referenceProps;

          if (typeof onFocus === 'function') {
            onFocus(event);
          }
          describeFocusedChild(event, true);
        },
        onBlur: (event: React.FocusEvent<HTMLElement>) => {
          const { onBlur } = referenceElementProps.referenceProps;

          if (typeof onBlur === 'function') {
            onBlur(event);
          }
          describeFocusedChild(event, false);
        },
      };

      if (asChildElement) {
        const { ref: referenceRef, onClick, ...restReferenceProps } = referenceProps;
        const childProps = asChildElement.props;

        return cloneElement(asChildElement, {
          ...mergeReferenceProps(childProps, restReferenceProps),
          ref: combinedRef<HTMLElement>([referenceRef, containerRef, childProps.ref]),
          onClick: (event: React.MouseEvent<HTMLElement>) => {
            childProps.onClick?.(event);
            handleReferenceClick(event, referenceElementProps);
          },
        });
      }

      return (
        <span
          { ...referenceProps }
          ref={ combinedRef<HTMLElement>([referenceProps.ref, containerRef]) }
          onClick={ (event) => handleReferenceClick(event, referenceElementProps) }
          className={ cn(styles.container, { [styles.errorContainer]: type === 'error' }, containerClassName) }
          style={ containerStyles }
        >
          { children }
        </span>
      );
    };

    const floatingElement = (
      <Floating
        ref={ ref }
        renderOnlyVisible
        controls={ {
          disabled,
          shouldOpen: () => shouldShowTooltip({ container: containerRef.current }),
        } }
        positioning={ {
          placement,
          usePortal,
          positionFixed,
          offsetOptions: computedOffsetOptions,
          shiftPadding: 0,
          preventOverflowEnabled,
          boundary,
        } }
        interactions={ {
          hover: {
            enabled: !isTouchDevice,
            handleClose: !hideOnTooltipMouseEnter,
          },
          focus: {
            enabled: !isTouchDevice,
          },
          role: 'tooltip',
          click: {
            enabled: isTouchDevice,
          },
          dismiss: {
            onOutsidePress: true,
            onAncestorScroll: true,
          },
        } }
        reference={ renderReference }
        floating={ ({ contentProps, placement, arrowProps }) => {
          const placementInCamelCase = camelCase(placement);

          return (
            <div
              className={ cn(
                styles.tooltip,
                {
                  [styles.error]: type === 'error',
                  [styles.nontouchable]: hideOnTooltipMouseEnter,
                  [styles.darkTheme]: dark,
                },
                className,
              ) }
              data-placement={ placement }
              data-test="tooltip"
              { ...contentProps }
              /**
               * The tooltip is portalled, but React still bubbles its events up the trigger's tree:
               * clicking or selecting its content must not trigger a clickable row or link around the trigger.
               */
              onClick={ (event) => event.stopPropagation() }
              style={ {
                ...contentProps.style,
                zIndex,
              } }
            >
              <div
                className={ cn(
                  styles.arrow,
                  styles[placementInCamelCase],
                ) }
                { ...arrowProps }
              />
              <div
                style={ { maxWidth } }
                className={ cn(styles.content, styles[placementInCamelCase]) }
              >
                { renderTooltipContent() }
              </div>
            </div>
          );
        } }
      />
    );

    if (asChildElement) {
      return floatingElement;
    }

    return (
      <span className={ wrpClassName } data-tooltip-container>
        { floatingElement }
      </span>
    );
  },
);

export default Tooltip;
